import { useRuntimeConfig } from '#imports'

import type { Marketplace } from '@backmarket/http-api'
import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'

import type { Features } from '@backmarket/nuxt-module-marketplace/features'

import '../../types'
import { getMarkets } from './getMarkets'

/**
 * Get the marketplace associated to the given country code.
 *
 * This function is especially useful for usage in Nitro context as its sibling
 * composable `useMarketplace` will not work there. The tradeoff is that you
 * are expected to pass the country code manually.
 *
 * @example
 * const url = new URL(event.node.req.url)
 * const market = getMarketFromDomain(url.origin)
 * const marketplace = getMarketplace(market.countryCode)
 *
 * @warning
 * For all usage as part of Nuxt context, use `useMarketplace` instead.
 */
export function getMarketplace(countryCode: MarketCountryCode) {
  const config = useRuntimeConfig().public
  const allMarkets = getMarkets()
  const market = allMarkets[countryCode]

  const marketplace: Marketplace = {
    marketplaceCode: market.marketplace,
    markets: Object.values(allMarkets).filter((m) => {
      return m.marketplace === market.marketplace
    }),
  }

  return {
    allMarkets,
    market,
    marketplace,
    features: config.marketplace.features[countryCode] as Features,
  }
}
